import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Search } from 'lucide-react';

import BathroomImg from '@/assets/room-style/bathroom.png';
import ArtDecoImage from '@/assets/style-settings/style_ArtDeco.png';
import BohoChicImage from '@/assets/style-settings/style_BohoChic.png';
import CoastalImage from '@/assets/style-settings/style_Coastal.webp';
import ContemporaryImage from '@/assets/style-settings/style_Contemporary.webp';
import CurvesAndOrganicImage from '@/assets/style-settings/style_CurvesAndOrganic.webp';
import ElegentImage from '@/assets/style-settings/style_Electical.webp';
import IndustrialImage from '@/assets/style-settings/style_Industrial.webp';
import MediterraneanImage from '@/assets/style-settings/style_Mediterranean.webp';
import MidCenturyModernImage from '@/assets/style-settings/style_MidCerntury.webp';
import MinimalistImage from '@/assets/style-settings/style_Minimalist.webp';
import MoroccanImage from '@/assets/style-settings/style_Moroccan.webp';
import RetroAndVintageImage from '@/assets/style-settings/style_RetroAndVintage.webp';
import RusticImage from '@/assets/style-settings/style_Rustic.webp';
import ScandinavianImage from '@/assets/style-settings/style_Scandinavian.webp';
import SustainableDesignImage from '@/assets/style-settings/style_SustainableDesign.webp';
import UrbanModernImage from '@/assets/style-settings/style_UrbanModern.webp';
import VictorianImage from '@/assets/style-settings/style_Victorian.webp';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { DESIRED_STYLE_OPTIONS } from '../../constants/style-settings';

export interface DesiredStyleSelectionContentProps {
  desiredStyle: DESIRED_STYLE_OPTIONS;
  handleUpdateDesiredStyle: (val?: DESIRED_STYLE_OPTIONS) => void;
}

const desiredStyleOptions = [
  { value: DESIRED_STYLE_OPTIONS.ELEGENT, image: ElegentImage },
  { value: DESIRED_STYLE_OPTIONS.SUSTAINABLE_DESIGN, image: SustainableDesignImage },
  { value: DESIRED_STYLE_OPTIONS.CURVES_AND_ORGANIC_SHAPES, image: CurvesAndOrganicImage },
  { value: DESIRED_STYLE_OPTIONS.RETRO_AND_VINTAGE, image: RetroAndVintageImage },
  { value: DESIRED_STYLE_OPTIONS.MINIMALIST, image: MinimalistImage },
  { value: DESIRED_STYLE_OPTIONS.INDUSTRIAL, image: IndustrialImage },
  { value: DESIRED_STYLE_OPTIONS.SCANDINAVIAN, image: ScandinavianImage },
  { value: DESIRED_STYLE_OPTIONS.RUSTIC, image: RusticImage },
  { value: DESIRED_STYLE_OPTIONS.CONTEMPORARY, image: ContemporaryImage },
  { value: DESIRED_STYLE_OPTIONS.MID_CENTURY_MODERN, image: MidCenturyModernImage },
  { value: DESIRED_STYLE_OPTIONS.FRENCH_COUNTRY, image: BathroomImg },
  { value: DESIRED_STYLE_OPTIONS.COASTAL, image: CoastalImage },
  { value: DESIRED_STYLE_OPTIONS.ART_DECO, image: ArtDecoImage },
  { value: DESIRED_STYLE_OPTIONS.BOHO_CHIC, image: BohoChicImage },
  { value: DESIRED_STYLE_OPTIONS.URBAN_MODERN, image: UrbanModernImage },
  { value: DESIRED_STYLE_OPTIONS.MEDITERRANEAN, image: MediterraneanImage },
  { value: DESIRED_STYLE_OPTIONS.MOROCCAN, image: MoroccanImage },
  { value: DESIRED_STYLE_OPTIONS.VICTORIAN, image: VictorianImage },
];

export const DesiredStyleSelectionContent = ({
  desiredStyle,
  handleUpdateDesiredStyle,
}: DesiredStyleSelectionContentProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string | undefined>();

  const filteredDesiredStyleOptions = useMemo(
    () =>
      !search
        ? desiredStyleOptions
        : desiredStyleOptions.filter((option) =>
            t(option).toLowerCase().includes(search.toLowerCase())
          ),
    [t, search]
  );

  return (
    <div className="w-full">
      <div className="flex h-[40px] items-center justify-between rounded-b-sm bg-text-primary pl-5 pr-6 lg:h-[60px]">
        <span className="text-sm font-semibold text-white">{t('desiredStyle')}</span>
      </div>
      <div className="bg-white p-2 sm:px-5 sm:py-6">
        <Input
          size="sm"
          className="border border-solid border-primary-light"
          placeholder={t('search')}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          startIcon={<Search className="text-primary-light" />}
        />
        <div className="mt-3 grid max-h-calc-full-minus-100 grid-cols-2 gap-2 overflow-y-auto sm:max-h-[400px] lg:max-h-[472px]">
          {filteredDesiredStyleOptions.map((option, index) => {
            const isSelected = option.value === desiredStyle;

            return (
              <div
                key={`desired-style-option-${index}`}
                className={cn(
                  'relative flex cursor-pointer flex-col overflow-hidden rounded-sm border border-solid',
                  !isSelected ? 'border-text-primary' : 'border-primary-light'
                )}
                onClick={() => handleUpdateDesiredStyle(option.value)}
              >
                <div className="flex-1">
                  <img className="h-[84px] w-full object-cover" src={option.image} />
                </div>
                <div
                  className={cn(
                    'z-2 flex h-[30px] items-center justify-center',
                    isSelected ? 'bg-primary-light' : 'bg-text-primary'
                  )}
                >
                  <span className="text-center text-sm font-semibold leading-none text-white">
                    {t(option.value)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
